import { z } from 'zod/mini'

export const faroSamplingRuleSchema = z.object({
  /**
   * Route prefix to match. Matching is prefix-based: configuring `'/payment'`
   * matches `'/payment'`, `'/payment/checkout'`, and
   * `'/payment/checkout/cart'`. Leading slash is optional.
   */
  route: z.string(),
  /**
   * Sampling rate for matched routes. Must be between 0 (never sample) and 1
   * (always sample).
   */
  samplingRate: z.number(),
})

export const faroSamplingRulesSchema = z.array(faroSamplingRuleSchema)

export type FaroSamplingRule = z.infer<typeof faroSamplingRuleSchema>

/**
 * Returns true when `routePath` falls under the subtree rooted at `ruleRoute`.
 *
 * The comparison is segment-exact: the rule route must either equal the
 * routePath, be a prefix followed by a `/`, or appear at the tail of a longer
 * path preceded by a `/`. This prevents `/payment` from accidentally matching
 * `/payment-redirect`.
 */
function routeMatchesPrefix(routePath: string, ruleRoute: string): boolean {
  const prefix = ruleRoute.startsWith('/') ? ruleRoute : `/${ruleRoute}`

  return (
    routePath === prefix ||
    routePath.startsWith(`${prefix}/`) ||
    routePath.endsWith(prefix) ||
    routePath.includes(`${prefix}/`)
  )
}

/**
 * Returns the sampling rate for the most-specific rule that matches
 * `routePath`, or `null` when no rule matches.
 *
 * "Most specific" is defined as the rule whose `route` string is longest
 * (more segments → more specific).
 */
export function getSamplingRateForRoute(
  rules: FaroSamplingRule[],
  routePath: string,
): number | null {
  let bestMatch: FaroSamplingRule | null = null

  for (const rule of rules) {
    if (routeMatchesPrefix(routePath, rule.route)) {
      if (bestMatch === null || rule.route.length > bestMatch.route.length) {
        bestMatch = rule
      }
    }
  }

  return bestMatch?.samplingRate ?? null
}
